import './App.css';
import { useEffect, useState } from 'react';
import LoginForm from './components/loginForm';
import ForgotPassword from './components/forgotPassword';
import ResetPassword from './components/resetPassword';
import SalesList from './components/sales/salesList';
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { ProtectedRoute } from './routes/protectedRoute';
import { UserProvider, useUser } from './context/userContext';
import { getConfig } from './api/systemConfigApi';
import MaintenancePage from './components/common/MaintenancePage';
import CreateSalesForm from './components/sales/createSalesForm';
import CommissionDetail from './components/commission/commissionDetail';
import AdminSalesList from './components/admin/adminSalesList';
import SalesCommissionList from './components/accountant/salesCommissionList';
import WebbeyCommissionList from './components/accountant/webbeyCommissionList';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import 'dayjs/locale/en-gb';
import { UserProfile } from './components/agent/userProfile';
import MainLayout from './components/layout/MainLayout';
import { NewDashboard } from './components/dashboard/newDashboard';
import CompetitiveAnalysis2026 from './components/saleskit/CompetitiveAnalysis2026';
import NotFound from './components/common/notFound';
import AsternClaimList from './components/accountant/asternClaimList';
import { AgentCommissionProgressList } from './components/commission/agentCommissionProgressList';
import MocList from './components/commission/mocList';
import GroupSalesList from './components/sales/groupSalesList';
// v1 RSC archived to _deprecated/ — V2 is the active component at /tools/rsc
import ResidentialSolarCalculatorV2 from './components/sales/residentialSolarCalculatorV2';
import CommercialSalesCalculator from './components/sales/commercialSolarCalculator';
import UserManagement from './components/admin/userManagement';
import CustomerList from './components/customer/customerList';
import AccountingOverview from './components/accountant/accountingOverview';
import AgentHierarchy from './components/admin/agentHierarchy';
import EditSalePage from './components/admin/editSalePage';
import EditUserPage from './components/admin/editUserPage';
import EditCommissionPage from './components/accountant/editCommissionPage';
import PricingPage from './components/resources/pricingTab';
import AddonsPage from './components/resources/addonsTab';
import DocumentsPage from './components/resources/documentsTab';
import PermissionManager from './components/settings/permissionManager';
import VersionInfo from './components/settings/versionInfo';
import NotificationList from './components/notifications/notificationList';
import SalesReport from './components/reports/salesReport';
import WebbeyReport from './components/reports/webbeyReport';
import PayoutReport from './components/reports/payoutReport';
import AgentReport from './components/reports/agentReport';
import PendingApprovals from './components/approvals/pendingApprovals';
import ApproverConfig from './components/settings/approverConfig';
import ImportSales from './components/settings/importSales';
import NoticeboardManagement from './components/settings/noticeboardManagement';
import ActiveCampaigns from './components/incentives/activeCampaigns';
import CampaignDetail from './components/incentives/campaignDetail';
import ManageCampaigns from './components/incentives/manageCampaigns';
import CampaignDetailPage from './components/incentives/campaignDetailPage';
import SalesCommissionDetail from './components/accounting/salesCommissionDetail';
import AgentSignup from './components/public/agentSignup';
import SignPage from './components/public/signPage';
import AgreementGate from './components/public/agreementGate';
import ApplicationStatus from './components/public/applicationStatus';
import AllSlipsList from './components/accountant/allSlipsList';
import Cp58List from './components/accountant/cp58List';
import QuotationPreview from './components/tools/quotationPreview';
import CommercialQuotationPreview from './components/tools/commercialQuotationPreview';
import useAppPolling from './hooks/useAppPolling';
import UpdateBanner from './components/common/updateBanner';
import FaqPage from './components/tools/faqPage';
import TutorialsReader from './components/learn/TutorialsReader';
import TutorialFormPage from './components/learn/TutorialFormPage';
import MyCommissionSlips from './components/commission/myCommissionSlips';
import MyIncentiveClaimsList from './components/commission/myIncentiveClaimsList';
import UserPermissions from './components/settings/userPermissions';
import ActivityLogs from './components/settings/activityLogs';
import CommissionReport from './components/reports/commissionReport';
import CommissionAgentDetail from './components/reports/commissionAgentDetail';
import TierSettings from './components/admin/tierSettings';
import WhatsappSettings from './components/settings/whatsappSettings';
import GeneralSettings from './components/settings/generalSettings';
import DesignRequestList from './components/design/DesignRequestList';
import RequestDesignForm from './components/design/RequestDesignForm';

function RedirectWithParams({ from, to }) {
  const params = useParams();
  const newPath = to + '/' + (params.salesId || '');
  return <Navigate to={newPath} replace />;
}

function AppContent() {
  const { updateAvailable } = useAppPolling();

  // Disable mouse-wheel value-stepping on all number inputs across the app.
  // Native HTML number inputs change value when the wheel fires over a focused
  // field — annoying when the user is mid-scroll. We blur on wheel so the page
  // scrolls instead and the input value stays put.
  useEffect(() => {
    const handler = () => {
      const el = document.activeElement;
      if (el && el.tagName === 'INPUT' && el.type === 'number') {
        el.blur();
      }
    };
    document.addEventListener('wheel', handler, { passive: true });
    return () => document.removeEventListener('wheel', handler);
  }, []);

  // ---- Maintenance mode gate ----
  // When Super Admin turns Maintenance Mode on, every logged-in non-Super-Admin user
  // sees the MaintenancePage, EXCEPT on pricing tools (/tools/*), resources
  // (/resources/*) and tutorials. Super Admin is never blocked (so they can turn it off).
  const { user } = useUser();
  const location = useLocation();
  const [maintenance, setMaintenance] = useState(false);
  useEffect(() => {
    let active = true;
    const check = () => getConfig('MaintenanceMode')
      .then(r => { if (active) setMaintenance(String(r?.data?.value).toLowerCase() === 'true'); })
      .catch(() => {});
    check();
    const t = setInterval(check, 60000);
    return () => { active = false; clearInterval(t); };
  }, [location.pathname]);
  const isSuperAdmin = !!user?.roles?.includes('Super Admin');
  const maintAllowed = ['/tools', '/resources', '/tutorials', '/user/profile'].some(p => location.pathname.startsWith(p));
  if (maintenance && user && !isSuperAdmin && !maintAllowed) {
    return <MaintenancePage />;
  }

  return (
    <div>
      <UpdateBanner show={updateAvailable} />
      <Routes>
        <Route path="/" element={<LoginForm />} />
        {/* /login is an alias for / so links from email templates ({BaseUrl}/login) land correctly */}
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/join/:token" element={<AgentSignup />} />
        <Route path="/sign/:token" element={<SignPage />} />
        <Route path="/accept-agreement" element={
          <ProtectedRoute bypassAgreementGate>
            <AgreementGate />
          </ProtectedRoute>
        } />
        <Route path="/application-status" element={
          <ProtectedRoute bypassApplicationGate bypassAgreementGate>
            <ApplicationStatus />
          </ProtectedRoute>
        } />
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <MainLayout>
              <NewDashboard />
            </MainLayout>
          </ProtectedRoute>
        } />
        {/* Login-gated competitive sales kit — full-screen, no sidebar (phone pitch). */}
        <Route path="/competitiveanalysis2026" element={
          <ProtectedRoute>
            <CompetitiveAnalysis2026 />
          </ProtectedRoute>
        } />
        <Route path="/sales" element={
          <ProtectedRoute requiredRoleRight={["AgentSalesList"]}>
            <MainLayout>
              <SalesList />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/sales/group" element={
          <ProtectedRoute requiredRoleRight={["GroupSalesList"]}>
            <MainLayout>
              <GroupSalesList />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/sales/create" element={
          <ProtectedRoute requiredRoleRight={["CreateSale"]}>
            <MainLayout>
              <CreateSalesForm />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/user/profile" element={
          <ProtectedRoute>
            <MainLayout>
              <UserProfile />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/commissions/psc" element={
          <ProtectedRoute requiredRoleRight={["PersonalSalesCommission"]}>
            <MainLayout>
              <AgentCommissionProgressList commPayType="PSC" />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/commissions/doc" element={
          <ProtectedRoute requiredRoleRight={["DownlineOverridingCommission"]}>
            <MainLayout>
              <AgentCommissionProgressList commPayType="DOC" />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/commissions/toc" element={
          <ProtectedRoute requiredRoleRight={["TeamOverridingCommission"]}>
            <MainLayout>
              <AgentCommissionProgressList commPayType="TOC" />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/commissions/moc" element={
          <ProtectedRoute requiredRoleRight={["ManagerOverridingCommission"]}>
            <MainLayout>
              <MocList />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/commissions/referral" element={
          <ProtectedRoute requiredRoleRight={["PersonalSalesCommission"]}>
            <MainLayout>
              <AgentCommissionProgressList commPayType="RefC" />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/commissions/detail/:commProgressId" element={
          <ProtectedRoute>
            <MainLayout>
              <CommissionDetail />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/commissions/mySlips" element={
          <ProtectedRoute requiredRoleRight={["PersonalSalesCommission"]}>
            <MainLayout>
              <MyCommissionSlips />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/commissions/incentives" element={
          <ProtectedRoute requiredRoleRight={["PersonalSalesCommission"]}>
            <MainLayout>
              <MyIncentiveClaimsList />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/sales/customers" element={
          <ProtectedRoute>
            <MainLayout>
              <CustomerList />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/accounting/customers" element={
          <ProtectedRoute>
            <MainLayout>
              <CustomerList />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/reports/commission" element={
          <ProtectedRoute requiredRole={["Accountant", "Super Admin", "Leader", "Sales Agent", "In-house Agent"]}>
            <MainLayout>
              <CommissionReport />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/reports/commission/agent/:agentId" element={
          <ProtectedRoute requiredRoleRight={["AllSalesCommList"]}>
            <MainLayout>
              <CommissionAgentDetail />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/accounting/sales" element={
          <ProtectedRoute requiredRoleRight={["AllSalesList"]}>
            <MainLayout>
              <AdminSalesList />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/accounting/sales/:salesId" element={
          <ProtectedRoute>
            <MainLayout>
              <EditSalePage />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/users" element={
          <ProtectedRoute requiredRoleRight={["UserManagement"]}>
            <MainLayout>
              <UserManagement />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/users/:userId" element={
          <ProtectedRoute requiredRoleRight={["UserManagement"]}>
            <MainLayout>
              <EditUserPage />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/hierarchy" element={
          <ProtectedRoute requiredRoleRight={["AgentHierarchy"]}>
            <MainLayout>
              <AgentHierarchy />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/tiers" element={
          <ProtectedRoute requiredRole={["Super Admin"]}>
            <MainLayout>
              <TierSettings />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/settings/whatsapp" element={
          <ProtectedRoute requiredRole={["Super Admin"]}>
            <MainLayout>
              <WhatsappSettings />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/settings/general" element={
          <ProtectedRoute requiredRole={["Super Admin"]}>
            <MainLayout>
              <GeneralSettings />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/design-requests" element={
          <ProtectedRoute requiredRoleRight={["DesignRequestAccess", "CreateSale"]}>
            <MainLayout>
              <DesignRequestList />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/design-requests/new" element={
          <ProtectedRoute requiredRoleRight={["DesignRequestAccess", "CreateSale"]}>
            <MainLayout>
              <RequestDesignForm />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/design-requests/:id/edit" element={
          <ProtectedRoute requiredRoleRight={["DesignRequestAccess", "CreateSale"]}>
            <MainLayout>
              <RequestDesignForm />
            </MainLayout>
          </ProtectedRoute>
        } />
<Route path="/accounting/asternClaims" element={
          <ProtectedRoute requiredRoleRight={["TotalCommission"]}>
            <MainLayout>
              <AsternClaimList />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/accounting/commission/:commProgressId" element={
          <ProtectedRoute requiredRoleRight={["AllSalesCommList"]}>
            <MainLayout>
              <EditCommissionPage />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/accounting/salesCommissions" element={
          <ProtectedRoute requiredRoleRight={["AllSalesList"]}>
            <MainLayout>
              <SalesCommissionList />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/accounting/webbeyCommissions" element={
          <ProtectedRoute requiredRoleRight={["WebbeyCommission"]}>
            <MainLayout>
              <WebbeyCommissionList />
            </MainLayout>
          </ProtectedRoute>
        } />
{/* Superseded by /accounting/slips (AllSlipsList). Kept as redirect so old
            bookmarks + CommissionSlip notification deep-links still land. */}
        <Route path="/accounting/commissionSlips" element={<Navigate to="/accounting/slips" replace />} />
        <Route path="/accounting/slips" element={
          <ProtectedRoute requiredRoleRight={["CommissionSlipGenerator", "PendingApprovals", "AllSalesCommList"]}>
            <MainLayout>
              <AllSlipsList />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/accounting/cp58" element={
          <ProtectedRoute requiredRoleRight={["Cp58Generator"]}>
            <MainLayout>
              <Cp58List />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/tools/csc" element={
          <ProtectedRoute>
            <MainLayout>
              <CommercialSalesCalculator />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/tools/rsc" element={
          <ProtectedRoute>
            <MainLayout>
              <ResidentialSolarCalculatorV2 />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/tools/faq" element={
          <ProtectedRoute>
            <MainLayout>
              <FaqPage />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/tutorials" element={
          <ProtectedRoute>
            <MainLayout>
              <TutorialsReader />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/tutorials/new" element={
          <ProtectedRoute>
            <MainLayout>
              <TutorialFormPage />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/tutorials/:id/edit" element={
          <ProtectedRoute>
            <MainLayout>
              <TutorialFormPage />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/quotation-preview" element={
          <ProtectedRoute>
            <QuotationPreview />
          </ProtectedRoute>
        } />
        <Route path="/commercial-quotation-preview" element={
          <ProtectedRoute>
            <CommercialQuotationPreview />
          </ProtectedRoute>
        } />

        <Route path="/settings/permissions" element={
          <ProtectedRoute requiredRoleRight={["UserManagement"]}>
            <MainLayout>
              <PermissionManager />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/settings/version" element={
          <ProtectedRoute>
            <MainLayout>
              <VersionInfo />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/settings/noticeboard" element={
          <ProtectedRoute requiredRoleRight={["NoticeBoard"]}>
            <MainLayout>
              <NoticeboardManagement />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/settings/user-permissions/:userId" element={
          <ProtectedRoute requiredRoleRight={["UserManagement"]}>
            <MainLayout>
              <UserPermissions />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/notifications" element={
          <ProtectedRoute>
            <MainLayout>
              <NotificationList />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/resources/pricing" element={
          <ProtectedRoute>
            <MainLayout>
              <PricingPage />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/resources/addons" element={
          <ProtectedRoute>
            <MainLayout>
              <AddonsPage />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/resources/documents" element={
          <ProtectedRoute>
            <MainLayout>
              <DocumentsPage />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/reports/sales" element={
          <ProtectedRoute requiredRole={["Accountant", "Super Admin", "Leader", "Sales Agent", "In-house Agent"]}>
            <MainLayout>
              <SalesReport />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/reports/webbey" element={
          <ProtectedRoute requiredRole={["Accountant", "Super Admin"]}>
            <MainLayout>
              <WebbeyReport />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/reports/payout" element={
          <ProtectedRoute requiredRole={["Accountant", "Super Admin"]}>
            <MainLayout>
              <PayoutReport />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/reports/agents" element={
          <ProtectedRoute requiredRole={["Accountant", "Super Admin"]}>
            <MainLayout>
              <AgentReport />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/approvals" element={
          <ProtectedRoute>
            <MainLayout>
              <PendingApprovals />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/settings/approvers" element={
          <ProtectedRoute requiredRoleRight={["UserManagement"]}>
            <MainLayout>
              <ApproverConfig />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/settings/import" element={
          <ProtectedRoute requiredRoleRight={["ImportSales", "UserManagement"]}>
            <MainLayout>
              <ImportSales />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/settings/logs" element={
          <ProtectedRoute requiredRoleRight={["UserManagement"]}>
            <MainLayout>
              <ActivityLogs />
            </MainLayout>
          </ProtectedRoute>
        } />

        {/* /admin/applications consolidated into /approvals "Agent Applications" tab (v1.5.24).
            Kept as redirect so any bookmarks/notification redirects still land somewhere sensible. */}
        <Route path="/admin/applications" element={<Navigate to="/approvals" replace />} />

        <Route path="/incentives" element={
          <ProtectedRoute>
            <MainLayout>
              <ActiveCampaigns />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/incentives/manage" element={
          <ProtectedRoute requiredRoleRight={["IncentiveManagement"]}>
            <MainLayout>
              <ManageCampaigns />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/incentives/slips" element={<Navigate to="/accounting/slips?type=Incentive" replace />} />
        {/* Legacy IncentiveSlips page deprecated — unified into /accounting/slips. */}
        <Route path="/incentives/slips/legacy" element={<Navigate to="/accounting/slips?type=Incentive" replace />} />
        <Route path="/incentives/:campaignId/detail" element={
          <ProtectedRoute>
            <MainLayout>
              <CampaignDetailPage />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/incentives/:id" element={
          <ProtectedRoute>
            <MainLayout>
              <CampaignDetail />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/accounting/sales/:salesId/commission" element={
          <ProtectedRoute requiredRoleRight={["AllSalesCommList"]}>
            <MainLayout>
              <SalesCommissionDetail />
            </MainLayout>
          </ProtectedRoute>
        } />

        {/* Redirects for old routes */}
        <Route path="/admin/sales/:salesId" element={<RedirectWithParams from="/admin/sales" to="/accounting/sales" />} />
        <Route path="/admin/sales" element={<Navigate to="/accounting/sales" replace />} />
        <Route path="/customers" element={<Navigate to="/sales/customers" replace />} />
        <Route path="/groupSales" element={<Navigate to="/sales/group" replace />} />
        <Route path="/accounting/applications" element={<Navigate to="/admin/applications" replace />} />

        <Route path="*" element={<NotFound />} />
      </Routes>
    </div>
  );
}

export default function App() {
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
      <UserProvider>
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </UserProvider>
    </LocalizationProvider>
  );
}
